import { ComponentProps } from "react";
import { Link as _Link, LinkProps, useSearch } from "wouter";

import * as S from "./styled";

// Avoid a Webpack warning by re-exporting types separately from runtime code.
// See https://github.com/webpack/webpack/issues/7378
export {
  Router,
  Route,
  Switch,
  Redirect,
  useLocation,
  useRoute,
  useParams,
  useSearch,
  useSearchParams,
} from "wouter";
export { useHashLocation } from "wouter/use-hash-location";
export type { StringRouteParams } from "wouter";

/**
 * Wrap wouter's <Link> component to ensure that an <a> element is always
 * created. This ensures that you can style a Link and its child component
 * without worrying about the styles being squished together in one component at
 * runtime. It's also better for accessibility, and enables <a>-dependent
 * features like "open in new tab" right-click actions in the browser.
 */
export function Link(props: LinkProps) {
  const {
    to,
    href,
    asChild,
    className: classNameProp,
    ref: _ref,
    ...rest
  } = props;

  const url = to ?? href;
  const isMailto = url?.toLowerCase().startsWith("mailto:");
  const isExternal =
    !!url &&
    !isMailto &&
    (() => {
      try {
        const parsedUrl = new URL(url, window.location.origin);
        return parsedUrl.origin !== window.location.origin;
      } catch {
        // If URL parsing fails, treat it as internal (e.g., relative links)
        return false;
      }
    })();

  // wouter puts URLs into the browser history stack. This fails for mailto
  // links and for links in a different origin. So just use plain links in that
  // case.
  if (isMailto || isExternal) {
    // Following react-router's API, wouter's className component can be
    // a function that receives a boolean indicating if the link is active.
    // mailto links are never active, so we can safely pass false for that case.
    // And we'll just silently ignore this case for external links because we
    // won't use this feature for links outside our app.
    const className = asChild
      ? undefined
      : typeof classNameProp === "function"
        ? classNameProp(false)
        : classNameProp;

    return (
      <a
        {...rest}
        className={className}
        target="_blank"
        rel="noreferrer"
        href={url}
      />
    );
  }

  return <_Link {...props} />;
}

/**
 * Hook to get the search query parameters from the URL.
 *
 * The returned object is a map of query parameter names to their values. All
 * values will be strings, so must be converted to their appropriate types
 * by the caller if required.
 */
export const useQueryParams = (): Record<string, string> => {
  const search = useSearch();
  const params: Record<string, string> = {};
  const s = new URLSearchParams(search);

  s.forEach((v, k) => {
    params[k] = v;
  });

  return params;
};

/**
 * A non-breaking space that doesn't underline on hover. Useful for separating,
 * for example, a project name and revision number badge in a link.
 */
export const NeverUnderlineSpace = () => (
  <S.NeverUnderline>&nbsp;</S.NeverUnderline>
);

type TextLinkProps = LinkProps & {
  variant: ComponentProps<typeof S.TextLink>["$variant"];
};

export const TextLink = (props: TextLinkProps) => {
  const { variant, ...rest } = props;
  return <S.TextLink $variant={variant} {...rest} />;
};

export const BlueLink = S.BlueLink;
